/**
 * Demo Project: 1-minute clip with animated limbs, text titles, effects
 * 
 * 1440 frames @ 24fps = 60 seconds
 * Step 1 (0-20s):   Blonde ballerina dances - "Baletka Agátka"
 * Step 2 (20-30s):  Transforms into Sun - "Čiperkové Sluníčko"
 * Step 3 (30-40s):  Sun -> Singer Girl - "Rebelka"
 * Step 4 (40-60s):  All three dance together & wave at the end
 */

const W = 800, H = 600;
const CX = W / 2, CY = H / 2 + 30;

// Helper: generate waving arm keyframes
function waveArm(startFrame, endFrame, step, min, max) {
  const kfs = [];
  let up = true;
  for (let f = startFrame; f <= endFrame; f += step) {
    kfs.push({ frame: f, val: up ? max : min });
    up = !up;
  }
  return kfs;
}

// Helper: generate walking legs keyframes
function walkLegs(startFrame, endFrame, step, amp) {
  const kfs = [];
  let phase = true;
  for (let f = startFrame; f <= endFrame; f += step) {
    kfs.push({ frame: f, leftVal: phase ? amp : -amp, rightVal: phase ? -amp : amp });
    phase = !phase;
  }
  return kfs;
}

export function createDemoProject() {
  // Pre-generate limb animations for ballerina step 1
  const bWaveR = waveArm(0, 480, 16, -30, 40);
  const bWaveL = waveArm(0, 480, 16, -20, 30);
  const bLegs = walkLegs(0, 480, 20, 15);

  // Step 4: All wave together (frames 960-1440)
  const allWaveR = waveArm(960, 1440, 14, -25, 50);
  const allWaveL = waveArm(960, 1440, 14, -15, 35);
  const allLegs = walkLegs(960, 1440, 18, 12);

  // Final wave (last 3 seconds = frames 1368-1440)
  const finalWaveR = waveArm(1368, 1440, 8, -10, 60);
  const finalWaveL = waveArm(1368, 1440, 8, -5, 50);

  return {
    name: "Show - Baletka, Slunicko & Zpevacka",
    width: W, height: H, fps: 24, totalFrames: 1440,
    layers: [
      // ===== STAGE & LIGHTING =====
      {
        id: "layer-stage", name: "Scena", visible: true, locked: false,
        objects: [
          // Stage floor - main wooden surface
          { id: "obj-stage-main", type: "rect", name: "Podium", x: CX, y: H - 60, width: 740, height: 80, rotation: 0, scaleX: 1, scaleY: 1, opacity: 1, fill: "#5c3d1e", stroke: "", strokeWidth: 0,
            keyframes: [{ frame: 0, x: CX, y: H - 60, rotation: 0, scaleX: 1, scaleY: 1, opacity: 1 }] },
          // Stage floor highlight strip
          { id: "obj-stage-top", type: "rect", name: "Podium povrch", x: CX, y: H - 85, width: 720, height: 30, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0.8, fill: "#8b6b42", stroke: "", strokeWidth: 0,
            keyframes: [{ frame: 0, x: CX, y: H - 85, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0.8 }] },
          // Stage floor reflection/gloss
          { id: "obj-stage-gloss", type: "rect", name: "Odlesk podia", x: CX, y: H - 92, width: 680, height: 8, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0.15, fill: "#fef3c7", stroke: "", strokeWidth: 0,
            keyframes: [{ frame: 0, x: CX, y: H - 92, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0.15 }] },
          // Stage front edge
          { id: "obj-stage-edge", type: "rect", name: "Hrana podia", x: CX, y: H - 22, width: 740, height: 6, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0.9, fill: "#3a2510", stroke: "", strokeWidth: 0,
            keyframes: [{ frame: 0, x: CX, y: H - 22, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0.9 }] },
          // Stage shadow
          { id: "obj-stage-shadow", type: "rect", name: "Stin podia", x: CX, y: H - 10, width: 760, height: 20, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0.3, fill: "#0a0a0a", stroke: "", strokeWidth: 0,
            keyframes: [{ frame: 0, x: CX, y: H - 10, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0.3 }] },

          // ---- FOOTLIGHTS (row of small lights) ----
          ...generateFootlights(),

          // ---- SPOTLIGHTS from above ----
          // Left spotlight - follows scene color
          { id: "obj-spot-left", type: "path", name: "Reflektor L", x: 220, y: 180, width: 160, height: 300, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0.08, fill: "#f9a8d4", stroke: "#f9a8d4", strokeWidth: 1,
            points: [0,-180, -80,180, 80,180, 0,-180],
            easing: "easeInOut",
            keyframes: [
              // Step 1: pink
              { frame: 0, x: 280, y: 200, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0 },
              { frame: 24, x: 280, y: 200, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0.08 },
              { frame: 460, x: 280, y: 200, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0.08 },
              { frame: 480, x: 280, y: 200, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0 },
              // Step 2: yellow
              { frame: 528, x: 300, y: 200, rotation: 0, scaleX: 1.1, scaleY: 1, opacity: 0.06 },
              { frame: 700, x: 300, y: 200, rotation: 0, scaleX: 1.1, scaleY: 1, opacity: 0.06 },
              { frame: 720, x: 300, y: 200, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0 },
              // Step 3: purple
              { frame: 768, x: 260, y: 200, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0.07 },
              { frame: 940, x: 260, y: 200, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0.07 },
              { frame: 960, x: 260, y: 200, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0 },
              // Step 4: pink on left character
              { frame: 984, x: 200, y: 200, rotation: 0, scaleX: 0.8, scaleY: 1, opacity: 0.1 },
              { frame: 1440, x: 200, y: 200, rotation: 0, scaleX: 0.8, scaleY: 1, opacity: 0.12 },
            ] },
          // Center spotlight
          { id: "obj-spot-center", type: "path", name: "Reflektor C", x: CX, y: 180, width: 180, height: 300, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0.08, fill: "#67e8f9", stroke: "#67e8f9", strokeWidth: 1,
            points: [0,-180, -90,200, 90,200, 0,-180],
            easing: "easeInOut",
            keyframes: [
              // Step 1: main center spot
              { frame: 0, x: CX, y: 200, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0 },
              { frame: 24, x: CX, y: 200, rotation: 0, scaleX: 1.1, scaleY: 1, opacity: 0.1 },
              { frame: 460, x: CX, y: 200, rotation: 0, scaleX: 1.1, scaleY: 1, opacity: 0.1 },
              { frame: 480, x: CX, y: 200, rotation: 0, scaleX: 1.2, scaleY: 1, opacity: 0 },
              // Step 2: warm spotlight for sun
              { frame: 528, x: CX, y: 190, rotation: 0, scaleX: 1.3, scaleY: 1, opacity: 0.12 },
              { frame: 700, x: CX, y: 190, rotation: 0, scaleX: 1.3, scaleY: 1, opacity: 0.12 },
              { frame: 720, x: CX, y: 200, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0 },
              // Step 3: purple for singer
              { frame: 768, x: CX, y: 200, rotation: 0, scaleX: 1.2, scaleY: 1, opacity: 0.09 },
              { frame: 940, x: CX, y: 200, rotation: 0, scaleX: 1.2, scaleY: 1, opacity: 0.09 },
              { frame: 960, x: CX, y: 200, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0 },
              // Step 4: center sun spot
              { frame: 984, x: CX, y: 190, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0.1 },
              { frame: 1440, x: CX, y: 190, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0.13 },
            ] },
          // Right spotlight
          { id: "obj-spot-right", type: "path", name: "Reflektor R", x: 580, y: 180, width: 160, height: 300, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0.08, fill: "#c4b5fd", stroke: "#c4b5fd", strokeWidth: 1,
            points: [0,-180, -80,180, 80,180, 0,-180],
            easing: "easeInOut",
            keyframes: [
              // Step 1: subtle side light
              { frame: 0, x: 520, y: 200, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0 },
              { frame: 24, x: 520, y: 200, rotation: 0, scaleX: 0.9, scaleY: 1, opacity: 0.05 },
              { frame: 460, x: 520, y: 200, rotation: 0, scaleX: 0.9, scaleY: 1, opacity: 0.05 },
              { frame: 480, x: 520, y: 200, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0 },
              // Step 2
              { frame: 528, x: 500, y: 200, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0.05 },
              { frame: 700, x: 500, y: 200, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0.05 },
              { frame: 720, x: 500, y: 200, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0 },
              // Step 3
              { frame: 768, x: 540, y: 200, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0.08 },
              { frame: 940, x: 540, y: 200, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0.08 },
              { frame: 960, x: 540, y: 200, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0 },
              // Step 4: right singer spot
              { frame: 984, x: 600, y: 200, rotation: 0, scaleX: 0.8, scaleY: 1, opacity: 0.1 },
              { frame: 1440, x: 600, y: 200, rotation: 0, scaleX: 0.8, scaleY: 1, opacity: 0.12 },
            ] },
        ]
      },
      // ===== BACKGROUND EFFECTS =====
      {
        id: "layer-bg", name: "Efekty", visible: true, locked: false,
        objects: [
          // Sparkle group - cycling sparkles throughout the animation
          ...generateSparkles(),
        ]
      },
      // ===== TEXT TITLES =====
      {
        id: "layer-text", name: "Titulky", visible: true, locked: false,
        objects: [
          // "Baletka Agátka" - Step 1
          { id: "obj-title1", type: "text", name: "Baletka Agátka", x: CX, y: 60, width: 300, height: 50, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0, fill: "#f9a8d4", text: "Baletka Agátka", fontSize: 32, stroke: "", strokeWidth: 0, easing: "easeInOut",
            keyframes: [
              { frame: 0, x: CX, y: 60, rotation: 0, scaleX: 0.8, scaleY: 0.8, opacity: 0 },
              { frame: 24, x: CX, y: 55, rotation: 0, scaleX: 1, scaleY: 1, opacity: 1 },
              { frame: 432, x: CX, y: 55, rotation: 0, scaleX: 1, scaleY: 1, opacity: 1 },
              { frame: 480, x: CX, y: 50, rotation: 0, scaleX: 1.1, scaleY: 1.1, opacity: 0 },
              { frame: 1440, x: CX, y: 50, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0 },
            ] },
          // "Čiperkové Sluníčko" - Step 2
          { id: "obj-title2", type: "text", name: "Čiperkové Sluníčko", x: CX, y: 60, width: 400, height: 50, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0, fill: "#fbbf24", text: "Čiperkové Sluníčko", fontSize: 32, stroke: "", strokeWidth: 0, easing: "easeInOut",
            keyframes: [
              { frame: 0, x: CX, y: 60, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0 },
              { frame: 480, x: CX, y: 60, rotation: 0, scaleX: 0.8, scaleY: 0.8, opacity: 0 },
              { frame: 528, x: CX, y: 55, rotation: 0, scaleX: 1, scaleY: 1, opacity: 1 },
              { frame: 672, x: CX, y: 55, rotation: 0, scaleX: 1, scaleY: 1, opacity: 1 },
              { frame: 720, x: CX, y: 50, rotation: 0, scaleX: 1.1, scaleY: 1.1, opacity: 0 },
              { frame: 1440, x: CX, y: 50, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0 },
            ] },
          // "Rebelka" - Step 3
          { id: "obj-title3", type: "text", name: "Rebelka", x: CX, y: 60, width: 200, height: 50, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0, fill: "#a78bfa", text: "Rebelka", fontSize: 36, stroke: "", strokeWidth: 0, easing: "easeInOut",
            keyframes: [
              { frame: 0, x: CX, y: 60, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0 },
              { frame: 720, x: CX, y: 65, rotation: 0, scaleX: 0.8, scaleY: 0.8, opacity: 0 },
              { frame: 768, x: CX, y: 55, rotation: -3, scaleX: 1.1, scaleY: 1.1, opacity: 1 },
              { frame: 912, x: CX, y: 55, rotation: 3, scaleX: 1.1, scaleY: 1.1, opacity: 1 },
              { frame: 960, x: CX, y: 50, rotation: 0, scaleX: 1.2, scaleY: 1.2, opacity: 0 },
              { frame: 1440, x: CX, y: 50, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0 },
            ] },
          // Final title
          { id: "obj-title-end", type: "text", name: "Konec", x: CX, y: 50, width: 200, height: 40, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0, fill: "#fde68a", text: "Děkujeme!", fontSize: 28, stroke: "", strokeWidth: 0, easing: "easeInOut",
            keyframes: [
              { frame: 0, x: CX, y: 50, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0 },
              { frame: 1368, x: CX, y: 60, rotation: 0, scaleX: 0.8, scaleY: 0.8, opacity: 0 },
              { frame: 1392, x: CX, y: 50, rotation: 0, scaleX: 1, scaleY: 1, opacity: 1 },
              { frame: 1440, x: CX, y: 50, rotation: 0, scaleX: 1, scaleY: 1, opacity: 1 },
            ] },
        ]
      },
      // ===== CHARACTERS =====
      {
        id: "layer-chars", name: "Postavy", visible: true, locked: false,
        objects: [
          // === BLONDE BALLERINA ===
          {
            id: "obj-blonde", type: "blondeBallerina", name: "Blond Baletka",
            x: CX, y: CY, width: 80, height: 160, rotation: 0, scaleX: 2, scaleY: 2,
            opacity: 1, fill: "#f9a8d4", stroke: "", strokeWidth: 0, easing: "easeInOut",
            keyframes: [
              // Step 1: Solo dance with limb animation (0-20s)
              { frame: 0, x: CX, y: CY, rotation: 0, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: 0, rightArmAngle: 0, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 16, x: CX, y: CY, rotation: 5, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: -20, rightArmAngle: 30, leftLegAngle: 12, rightLegAngle: -12 },
              { frame: 32, x: CX, y: CY, rotation: -5, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: 25, rightArmAngle: -15, leftLegAngle: -12, rightLegAngle: 12 },
              { frame: 48, x: CX, y: CY, rotation: 8, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: -30, rightArmAngle: 40, leftLegAngle: 15, rightLegAngle: -10 },
              // Move right with waving
              { frame: 72, x: CX + 80, y: CY, rotation: 0, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: 10, rightArmAngle: -20, leftLegAngle: -15, rightLegAngle: 15 },
              { frame: 88, x: CX + 80, y: CY - 15, rotation: 90, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: -40, rightArmAngle: 50, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 104, x: CX + 80, y: CY - 25, rotation: 180, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: 30, rightArmAngle: -30, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 120, x: CX + 80, y: CY, rotation: 360, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: 0, rightArmAngle: 0, leftLegAngle: 0, rightLegAngle: 0 },
              // Move left with walking legs
              { frame: 144, x: CX - 20, y: CY, rotation: 355, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: -15, rightArmAngle: 15, leftLegAngle: 12, rightLegAngle: -12 },
              { frame: 160, x: CX - 60, y: CY, rotation: 350, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: 20, rightArmAngle: -20, leftLegAngle: -12, rightLegAngle: 12 },
              { frame: 176, x: CX - 80, y: CY, rotation: 355, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: -25, rightArmAngle: 30, leftLegAngle: 15, rightLegAngle: -8 },
              { frame: 192, x: CX - 80, y: CY - 20, rotation: 0, scaleX: 2.1, scaleY: 2.1, opacity: 1, leftArmAngle: 35, rightArmAngle: -10, leftLegAngle: 0, rightLegAngle: 0 },
              // Center leap
              { frame: 220, x: CX - 20, y: CY, rotation: 0, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: -20, rightArmAngle: 20, leftLegAngle: 10, rightLegAngle: -10 },
              { frame: 240, x: CX, y: CY - 45, rotation: 0, scaleX: 2.2, scaleY: 2.2, opacity: 1, leftArmAngle: -50, rightArmAngle: -50, leftLegAngle: -20, rightLegAngle: 20 },
              { frame: 264, x: CX, y: CY, rotation: 0, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: 0, rightArmAngle: 0, leftLegAngle: 0, rightLegAngle: 0 },
              // Bow sequence
              { frame: 300, x: CX, y: CY, rotation: 0, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: 10, rightArmAngle: 10, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 324, x: CX, y: CY, rotation: 15, scaleX: 2, scaleY: 1.8, opacity: 1, leftArmAngle: 40, rightArmAngle: 40, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 348, x: CX, y: CY, rotation: 0, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: 0, rightArmAngle: 0, leftLegAngle: 0, rightLegAngle: 0 },
              // Double pirouette with arm extensions
              { frame: 384, x: CX, y: CY - 10, rotation: 180, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: -60, rightArmAngle: -60, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 420, x: CX, y: CY - 10, rotation: 540, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: 30, rightArmAngle: 30, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 456, x: CX, y: CY, rotation: 720, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: 0, rightArmAngle: 50, leftLegAngle: 0, rightLegAngle: 0 },
              // Step 2: Fade out
              { frame: 480, x: CX, y: CY, rotation: 720, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: -20, rightArmAngle: 60, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 528, x: CX, y: CY, rotation: 720, scaleX: 3, scaleY: 3, opacity: 0, leftArmAngle: 0, rightArmAngle: 0, leftLegAngle: 0, rightLegAngle: 0 },
              // Hidden
              { frame: 936, x: 200, y: CY, rotation: 0, scaleX: 1.5, scaleY: 1.5, opacity: 0, leftArmAngle: 0, rightArmAngle: 0, leftLegAngle: 0, rightLegAngle: 0 },
              // Step 4: Reappear left
              { frame: 960, x: 200, y: CY, rotation: 0, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 0, rightArmAngle: 0, leftLegAngle: 0, rightLegAngle: 0 },
              // Dance together with waving
              { frame: 984, x: 200, y: CY, rotation: 5, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: -20, rightArmAngle: 35, leftLegAngle: 10, rightLegAngle: -10 },
              { frame: 1008, x: 200, y: CY - 10, rotation: -5, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 25, rightArmAngle: -15, leftLegAngle: -10, rightLegAngle: 10 },
              { frame: 1032, x: 200, y: CY, rotation: 5, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: -25, rightArmAngle: 40, leftLegAngle: 12, rightLegAngle: -12 },
              { frame: 1056, x: 200, y: CY - 15, rotation: -5, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 30, rightArmAngle: -20, leftLegAngle: -12, rightLegAngle: 12 },
              { frame: 1080, x: 200, y: CY, rotation: 5, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: -15, rightArmAngle: 35, leftLegAngle: 10, rightLegAngle: -10 },
              { frame: 1104, x: 200, y: CY - 10, rotation: -5, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 25, rightArmAngle: -15, leftLegAngle: -10, rightLegAngle: 10 },
              { frame: 1128, x: 200, y: CY, rotation: 5, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: -20, rightArmAngle: 40, leftLegAngle: 12, rightLegAngle: -12 },
              { frame: 1152, x: 200, y: CY - 15, rotation: -5, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 30, rightArmAngle: -15, leftLegAngle: -12, rightLegAngle: 12 },
              { frame: 1176, x: 200, y: CY, rotation: 3, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: -25, rightArmAngle: 35, leftLegAngle: 10, rightLegAngle: -10 },
              // Group pirouette
              { frame: 1200, x: 200, y: CY, rotation: 0, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: -40, rightArmAngle: -40, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 1224, x: 200, y: CY - 10, rotation: 180, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 20, rightArmAngle: 20, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 1248, x: 200, y: CY, rotation: 360, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 0, rightArmAngle: 0, leftLegAngle: 0, rightLegAngle: 0 },
              // Sway
              { frame: 1272, x: 200, y: CY, rotation: 5, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: -15, rightArmAngle: 30, leftLegAngle: 8, rightLegAngle: -8 },
              { frame: 1296, x: 200, y: CY, rotation: -5, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 20, rightArmAngle: -10, leftLegAngle: -8, rightLegAngle: 8 },
              { frame: 1320, x: 200, y: CY, rotation: 5, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: -20, rightArmAngle: 35, leftLegAngle: 10, rightLegAngle: -10 },
              { frame: 1344, x: 200, y: CY, rotation: -3, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 25, rightArmAngle: -15, leftLegAngle: -8, rightLegAngle: 8 },
              // Final wave!
              { frame: 1368, x: 200, y: CY, rotation: 0, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: -10, rightArmAngle: -10, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 1380, x: 200, y: CY, rotation: 0, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: -10, rightArmAngle: 60, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 1392, x: 200, y: CY, rotation: 0, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: -10, rightArmAngle: 40, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 1404, x: 200, y: CY, rotation: 0, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: -10, rightArmAngle: 65, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 1416, x: 200, y: CY, rotation: 0, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: -10, rightArmAngle: 35, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 1428, x: 200, y: CY, rotation: 0, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: -10, rightArmAngle: 60, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 1440, x: 200, y: CY, rotation: 0, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: -10, rightArmAngle: 50, leftLegAngle: 0, rightLegAngle: 0 },
            ]
          },
          // === SUN ===
          {
            id: "obj-sun", type: "sun", name: "Slunicko",
            x: CX, y: CY - 40, width: 100, height: 100, rotation: 0, scaleX: 0.5, scaleY: 0.5,
            opacity: 0, fill: "#fbbf24", stroke: "", strokeWidth: 0, easing: "easeInOut",
            keyframes: [
              { frame: 0, x: CX, y: CY - 40, rotation: 0, scaleX: 0.5, scaleY: 0.5, opacity: 0, leftArmAngle: 0, rightArmAngle: 0 },
              { frame: 480, x: CX, y: CY - 40, rotation: 0, scaleX: 0.5, scaleY: 0.5, opacity: 0, leftArmAngle: 0, rightArmAngle: 0 },
              // Step 2: Appear with waving arms
              { frame: 528, x: CX, y: CY - 40, rotation: 0, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: 0, rightArmAngle: 0 },
              { frame: 544, x: CX, y: CY - 50, rotation: 8, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: -30, rightArmAngle: 40 },
              { frame: 560, x: CX, y: CY - 35, rotation: -8, scaleX: 2.1, scaleY: 2.1, opacity: 1, leftArmAngle: 25, rightArmAngle: -20 },
              { frame: 576, x: CX, y: CY - 50, rotation: 8, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: -35, rightArmAngle: 45 },
              { frame: 592, x: CX + 40, y: CY - 40, rotation: -5, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: 20, rightArmAngle: -15 },
              { frame: 608, x: CX + 40, y: CY - 50, rotation: 10, scaleX: 2.1, scaleY: 2.1, opacity: 1, leftArmAngle: -40, rightArmAngle: 50 },
              { frame: 624, x: CX, y: CY - 40, rotation: -10, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: 30, rightArmAngle: -25 },
              { frame: 640, x: CX - 40, y: CY - 45, rotation: 5, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: -25, rightArmAngle: 35 },
              { frame: 656, x: CX - 40, y: CY - 35, rotation: -5, scaleX: 2.1, scaleY: 2.1, opacity: 1, leftArmAngle: 20, rightArmAngle: -20 },
              { frame: 672, x: CX, y: CY - 40, rotation: 0, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: -15, rightArmAngle: 25 },
              { frame: 696, x: CX, y: CY - 40, rotation: 0, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: 0, rightArmAngle: 0 },
              // Fade out
              { frame: 720, x: CX, y: CY - 40, rotation: 0, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: 0, rightArmAngle: 0 },
              { frame: 768, x: CX, y: CY - 40, rotation: 360, scaleX: 0.5, scaleY: 0.5, opacity: 0, leftArmAngle: 0, rightArmAngle: 0 },
              // Step 4: Reappear center-top
              { frame: 936, x: CX, y: CY - 60, rotation: 0, scaleX: 1.3, scaleY: 1.3, opacity: 0, leftArmAngle: 0, rightArmAngle: 0 },
              { frame: 960, x: CX, y: CY - 60, rotation: 0, scaleX: 1.3, scaleY: 1.3, opacity: 1, leftArmAngle: 0, rightArmAngle: 0 },
              // Dance with waving
              { frame: 984, x: CX, y: CY - 65, rotation: 8, scaleX: 1.3, scaleY: 1.3, opacity: 1, leftArmAngle: -25, rightArmAngle: 35 },
              { frame: 1008, x: CX, y: CY - 55, rotation: -8, scaleX: 1.4, scaleY: 1.4, opacity: 1, leftArmAngle: 20, rightArmAngle: -15 },
              { frame: 1032, x: CX, y: CY - 65, rotation: 8, scaleX: 1.3, scaleY: 1.3, opacity: 1, leftArmAngle: -30, rightArmAngle: 40 },
              { frame: 1056, x: CX, y: CY - 55, rotation: -8, scaleX: 1.4, scaleY: 1.4, opacity: 1, leftArmAngle: 25, rightArmAngle: -20 },
              { frame: 1080, x: CX, y: CY - 65, rotation: 8, scaleX: 1.3, scaleY: 1.3, opacity: 1, leftArmAngle: -25, rightArmAngle: 35 },
              { frame: 1104, x: CX, y: CY - 55, rotation: -8, scaleX: 1.4, scaleY: 1.4, opacity: 1, leftArmAngle: 20, rightArmAngle: -15 },
              { frame: 1128, x: CX, y: CY - 65, rotation: 8, scaleX: 1.3, scaleY: 1.3, opacity: 1, leftArmAngle: -30, rightArmAngle: 40 },
              { frame: 1152, x: CX, y: CY - 55, rotation: -8, scaleX: 1.4, scaleY: 1.4, opacity: 1, leftArmAngle: 25, rightArmAngle: -20 },
              { frame: 1200, x: CX, y: CY - 60, rotation: 0, scaleX: 1.3, scaleY: 1.3, opacity: 1, leftArmAngle: 0, rightArmAngle: 0 },
              // Spin
              { frame: 1224, x: CX, y: CY - 60, rotation: 180, scaleX: 1.3, scaleY: 1.3, opacity: 1, leftArmAngle: -20, rightArmAngle: -20 },
              { frame: 1248, x: CX, y: CY - 60, rotation: 360, scaleX: 1.3, scaleY: 1.3, opacity: 1, leftArmAngle: 0, rightArmAngle: 0 },
              // Sway
              { frame: 1272, x: CX, y: CY - 65, rotation: 6, scaleX: 1.3, scaleY: 1.3, opacity: 1, leftArmAngle: -20, rightArmAngle: 30 },
              { frame: 1296, x: CX, y: CY - 55, rotation: -6, scaleX: 1.4, scaleY: 1.4, opacity: 1, leftArmAngle: 15, rightArmAngle: -10 },
              { frame: 1320, x: CX, y: CY - 65, rotation: 5, scaleX: 1.3, scaleY: 1.3, opacity: 1, leftArmAngle: -25, rightArmAngle: 35 },
              { frame: 1344, x: CX, y: CY - 55, rotation: -5, scaleX: 1.4, scaleY: 1.4, opacity: 1, leftArmAngle: 20, rightArmAngle: -15 },
              // Final wave
              { frame: 1368, x: CX, y: CY - 60, rotation: 0, scaleX: 1.4, scaleY: 1.4, opacity: 1, leftArmAngle: 0, rightArmAngle: 0 },
              { frame: 1380, x: CX, y: CY - 60, rotation: 0, scaleX: 1.4, scaleY: 1.4, opacity: 1, leftArmAngle: 50, rightArmAngle: 55 },
              { frame: 1392, x: CX, y: CY - 60, rotation: 0, scaleX: 1.4, scaleY: 1.4, opacity: 1, leftArmAngle: 30, rightArmAngle: 35 },
              { frame: 1404, x: CX, y: CY - 60, rotation: 0, scaleX: 1.4, scaleY: 1.4, opacity: 1, leftArmAngle: 55, rightArmAngle: 60 },
              { frame: 1416, x: CX, y: CY - 60, rotation: 0, scaleX: 1.4, scaleY: 1.4, opacity: 1, leftArmAngle: 30, rightArmAngle: 35 },
              { frame: 1428, x: CX, y: CY - 60, rotation: 0, scaleX: 1.4, scaleY: 1.4, opacity: 1, leftArmAngle: 55, rightArmAngle: 60 },
              { frame: 1440, x: CX, y: CY - 60, rotation: 0, scaleX: 1.4, scaleY: 1.4, opacity: 1, leftArmAngle: 45, rightArmAngle: 50 },
            ]
          },
          // === SINGER GIRL ===
          {
            id: "obj-singer", type: "singer", name: "Zpevacka",
            x: CX, y: CY, width: 80, height: 160, rotation: 0, scaleX: 0.5, scaleY: 0.5,
            opacity: 0, fill: "#8b5cf6", stroke: "", strokeWidth: 0, easing: "easeInOut",
            keyframes: [
              { frame: 0, x: CX, y: CY, rotation: 0, scaleX: 0.5, scaleY: 0.5, opacity: 0, leftArmAngle: 0, rightArmAngle: 0, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 720, x: CX, y: CY, rotation: 0, scaleX: 0.5, scaleY: 0.5, opacity: 0, leftArmAngle: 0, rightArmAngle: 0, leftLegAngle: 0, rightLegAngle: 0 },
              // Step 3: Appear
              { frame: 768, x: CX, y: CY, rotation: 0, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: 0, rightArmAngle: 0, leftLegAngle: 0, rightLegAngle: 0 },
              // Singing with sway and arm movement
              { frame: 784, x: CX, y: CY, rotation: -6, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: -15, rightArmAngle: 20, leftLegAngle: 8, rightLegAngle: -8 },
              { frame: 800, x: CX, y: CY, rotation: 6, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: 20, rightArmAngle: -10, leftLegAngle: -8, rightLegAngle: 8 },
              { frame: 816, x: CX + 30, y: CY, rotation: -4, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: -20, rightArmAngle: 25, leftLegAngle: 10, rightLegAngle: -10 },
              { frame: 832, x: CX + 30, y: CY, rotation: 4, scaleX: 2.1, scaleY: 2.1, opacity: 1, leftArmAngle: 15, rightArmAngle: -8, leftLegAngle: -10, rightLegAngle: 10 },
              { frame: 848, x: CX - 30, y: CY, rotation: -5, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: -25, rightArmAngle: 30, leftLegAngle: 12, rightLegAngle: -12 },
              { frame: 864, x: CX - 30, y: CY, rotation: 5, scaleX: 2.1, scaleY: 2.1, opacity: 1, leftArmAngle: 20, rightArmAngle: -10, leftLegAngle: -12, rightLegAngle: 12 },
              { frame: 880, x: CX, y: CY - 10, rotation: -3, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: -20, rightArmAngle: 25, leftLegAngle: 8, rightLegAngle: -8 },
              { frame: 896, x: CX, y: CY, rotation: 3, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: 15, rightArmAngle: -5, leftLegAngle: -8, rightLegAngle: 8 },
              { frame: 936, x: CX, y: CY, rotation: 0, scaleX: 2, scaleY: 2, opacity: 1, leftArmAngle: 0, rightArmAngle: 0, leftLegAngle: 0, rightLegAngle: 0 },
              // Step 4: Move right
              { frame: 960, x: 600, y: CY, rotation: 0, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 0, rightArmAngle: 0, leftLegAngle: 0, rightLegAngle: 0 },
              // Dance together with swaying
              { frame: 984, x: 600, y: CY, rotation: -5, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: -20, rightArmAngle: 30, leftLegAngle: 10, rightLegAngle: -10 },
              { frame: 1008, x: 600, y: CY - 10, rotation: 5, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 25, rightArmAngle: -15, leftLegAngle: -10, rightLegAngle: 10 },
              { frame: 1032, x: 600, y: CY, rotation: -5, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: -25, rightArmAngle: 35, leftLegAngle: 12, rightLegAngle: -12 },
              { frame: 1056, x: 600, y: CY - 15, rotation: 5, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 20, rightArmAngle: -10, leftLegAngle: -12, rightLegAngle: 12 },
              { frame: 1080, x: 600, y: CY, rotation: -5, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: -20, rightArmAngle: 30, leftLegAngle: 10, rightLegAngle: -10 },
              { frame: 1104, x: 600, y: CY - 10, rotation: 5, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 25, rightArmAngle: -15, leftLegAngle: -10, rightLegAngle: 10 },
              { frame: 1128, x: 600, y: CY, rotation: -5, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: -25, rightArmAngle: 35, leftLegAngle: 12, rightLegAngle: -12 },
              { frame: 1152, x: 600, y: CY - 10, rotation: 5, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 20, rightArmAngle: -10, leftLegAngle: -12, rightLegAngle: 12 },
              { frame: 1200, x: 600, y: CY, rotation: 0, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 0, rightArmAngle: 0, leftLegAngle: 0, rightLegAngle: 0 },
              // Spin
              { frame: 1224, x: 600, y: CY - 10, rotation: -180, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: -30, rightArmAngle: -30, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 1248, x: 600, y: CY, rotation: -360, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 0, rightArmAngle: 0, leftLegAngle: 0, rightLegAngle: 0 },
              // Sway
              { frame: 1272, x: 600, y: CY, rotation: -5, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: -20, rightArmAngle: 30, leftLegAngle: 8, rightLegAngle: -8 },
              { frame: 1296, x: 600, y: CY, rotation: 5, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 15, rightArmAngle: -10, leftLegAngle: -8, rightLegAngle: 8 },
              { frame: 1320, x: 600, y: CY, rotation: -5, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: -25, rightArmAngle: 35, leftLegAngle: 10, rightLegAngle: -10 },
              { frame: 1344, x: 600, y: CY, rotation: 3, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 20, rightArmAngle: -15, leftLegAngle: -8, rightLegAngle: 8 },
              // Final wave
              { frame: 1368, x: 600, y: CY, rotation: 0, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: -10, rightArmAngle: -10, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 1380, x: 600, y: CY, rotation: 0, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 55, rightArmAngle: 60, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 1392, x: 600, y: CY, rotation: 0, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 35, rightArmAngle: 40, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 1404, x: 600, y: CY, rotation: 0, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 60, rightArmAngle: 65, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 1416, x: 600, y: CY, rotation: 0, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 35, rightArmAngle: 40, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 1428, x: 600, y: CY, rotation: 0, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 55, rightArmAngle: 60, leftLegAngle: 0, rightLegAngle: 0 },
              { frame: 1440, x: 600, y: CY, rotation: 0, scaleX: 1.5, scaleY: 1.5, opacity: 1, leftArmAngle: 50, rightArmAngle: 55, leftLegAngle: 0, rightLegAngle: 0 },
            ]
          },
        ]
      },
    ]
  };
}

// Generate row of footlights along the front edge of the stage
function generateFootlights() {
  const lights = [];
  const count = 9;
  const startX = 100;
  const endX = 700;
  const spacing = (endX - startX) / (count - 1);
  const y = H - 100;
  const colors = ['#fde68a', '#f9a8d4', '#67e8f9', '#fde68a', '#c4b5fd', '#fde68a', '#67e8f9', '#f9a8d4', '#fde68a'];

  for (let i = 0; i < count; i++) {
    const x = startX + i * spacing;
    const color = colors[i % colors.length];
    const phaseOffset = i * 60;

    const kfs = [];
    // Pulsing glow effect throughout animation
    for (let f = 0; f <= 1440; f += 120) {
      const adjusted = (f + phaseOffset) % 240;
      const brightness = adjusted < 120 ? 0.4 + 0.3 * (adjusted / 120) : 0.7 - 0.3 * ((adjusted - 120) / 120);
      kfs.push({
        frame: Math.min(f, 1440),
        x, y,
        rotation: 0, scaleX: 1, scaleY: 1,
        opacity: f < 24 ? 0 : brightness,
      });
    }
    // Ensure first and last frames
    if (kfs[0].frame !== 0) kfs.unshift({ frame: 0, x, y, rotation: 0, scaleX: 1, scaleY: 1, opacity: 0 });

    // Deduplicate
    const seen = new Set();
    const uniqueKfs = kfs.filter(k => { if (seen.has(k.frame)) return false; seen.add(k.frame); return true; });

    lights.push({
      id: `obj-footlight-${i}`,
      type: "ellipse",
      name: `Rampa ${i + 1}`,
      x, y,
      width: 24, height: 10,
      rotation: 0, scaleX: 1, scaleY: 1,
      opacity: 0,
      fill: color,
      stroke: "", strokeWidth: 0,
      keyframes: uniqueKfs,
    });
  }
  return lights;
}

// Generate sparkles and effects scattered throughout the animation
function generateSparkles() {
  const sparkles = [];
  const positions = [
    { x: 120, y: 100 }, { x: 680, y: 80 }, { x: 300, y: 70 },
    { x: 550, y: 110 }, { x: 180, y: 140 }, { x: 620, y: 150 },
    { x: 400, y: 60 }, { x: 250, y: 130 }, { x: 500, y: 90 },
    { x: 700, y: 120 }, { x: 100, y: 85 }, { x: 450, y: 75 },
  ];
  const colors = ['#fde68a', '#fbcfe8', '#c4b5fd', '#67e8f9', '#fca5a5', '#86efac', '#fbbf24', '#f9a8d4', '#a78bfa', '#34d399', '#f472b6', '#60a5fa'];

  for (let i = 0; i < positions.length; i++) {
    const p = positions[i];
    const color = colors[i % colors.length];
    // Each sparkle appears at a different phase
    const startOffset = i * 90;
    const kfs = [];
    for (let cycle = 0; cycle < 5; cycle++) {
      const base = startOffset + cycle * 300;
      if (base > 1440) break;
      kfs.push({ frame: Math.min(base, 1440), x: p.x, y: p.y, rotation: 0, scaleX: 0.3, scaleY: 0.3, opacity: 0 });
      kfs.push({ frame: Math.min(base + 12, 1440), x: p.x, y: p.y - 8, rotation: 30, scaleX: 0.8, scaleY: 0.8, opacity: 0.8 });
      kfs.push({ frame: Math.min(base + 24, 1440), x: p.x, y: p.y - 16, rotation: 60, scaleX: 1.2, scaleY: 1.2, opacity: 1 });
      kfs.push({ frame: Math.min(base + 36, 1440), x: p.x, y: p.y - 24, rotation: 90, scaleX: 0.6, scaleY: 0.6, opacity: 0.3 });
      kfs.push({ frame: Math.min(base + 48, 1440), x: p.x, y: p.y - 30, rotation: 120, scaleX: 0.2, scaleY: 0.2, opacity: 0 });
    }
    // Deduplicate frames
    const seen = new Set();
    const uniqueKfs = kfs.filter(k => { if (seen.has(k.frame)) return false; seen.add(k.frame); return true; });

    sparkles.push({
      id: `obj-sparkle-${i}`,
      type: "star",
      name: `Jiskra ${i + 1}`,
      x: p.x, y: p.y,
      width: 16, height: 16,
      rotation: 0, scaleX: 0.3, scaleY: 0.3,
      opacity: 0,
      fill: color,
      numPoints: 4, innerRadius: 3, outerRadius: 8,
      stroke: "", strokeWidth: 0,
      keyframes: uniqueKfs,
    });
  }
  return sparkles;
}

export function createEmptyProject() {
  return {
    name: "Novy projekt",
    width: 800, height: 600, fps: 24, totalFrames: 240,
    layers: [{ id: "layer-1", name: "Vrstva 1", visible: true, locked: false, objects: [] }]
  };
}
